<template>
	<section id="products" class="section">
		<div class="container">
			<header v-reveal class="section__head">
				<p class="section__eyebrow">{{ t('products.eyebrow') }}</p>
				<h2 class="section__title">{{ t('products.title') }}</h2>
				<p class="section__subtitle">{{ t('products.subtitle') }}</p>
			</header>

			<div class="products__grid">
				<article
					v-for="(sector, index) in SECTORS"
					:key="sector.key"
					v-reveal="index * 70"
					:style="{ '--accent': sector.color }"
					class="product-card"
				>
					<img
						:src="assetUrl(sector.logo)"
						:alt="t(`products.sectors.${sector.key}.name`)"
						class="product-card__logo"
						loading="lazy"
					>
					<h3 class="product-card__name">
						{{ t(`products.sectors.${sector.key}.name`) }}
					</h3>
					<p class="product-card__system">
						{{ t(`products.sectors.${sector.key}.system`) }}
					</p>
					<span class="product-card__tag">
						{{ t(`products.sectors.${sector.key}.tag`) }}
					</span>
				</article>
			</div>
		</div>
	</section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { SECTORS } from '@/helpers/constants'
import { assetUrl } from '@/helpers/functions'

defineOptions({ name: 'ProductsSection' })

const { t } = useI18n()
</script>

<style scoped lang="scss">
.products__grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 258px), 1fr));
	gap: clamp(18px, 2.4vw, 28px);
}

.product-card {
	position: relative;
	overflow: hidden;
	padding: clamp(24px, 2.6vw, 32px);
	background: $color-paper;
	border: 1px solid $color-line;
	border-radius: $radius-lg;
	box-shadow: var(--shadow-sm);
	transition:
		transform 0.35s $ease,
		box-shadow 0.35s $ease,
		border-color 0.3s;

	&::after {
		content: '';
		position: absolute;
		inset: auto 0 0 0;
		height: 4px;
		background: var(--accent, #{$color-gold});
		transform: scaleX(0);
		transform-origin: left center;
		transition: transform 0.4s $ease;
	}

	@include rtl {
		&::after {
			transform-origin: right center;
		}
	}

	&:hover {
		transform: translateY(-6px);
		box-shadow: var(--shadow-md);
		border-color: transparent;
	}

	&:hover::after {
		transform: scaleX(1);
	}
}

.product-card__logo {
	height: 58px;
	width: auto;
	max-width: 82%;
	object-fit: contain;
	object-position: left center;

	@include rtl {
		object-position: right center;
	}
}

.product-card__name {
	margin-top: 22px;
	font-size: 1.22rem;
	font-weight: 700;
	color: var(--accent, #{$color-ink});
}

.product-card__system {
	margin-top: 8px;
	font-size: 0.96rem;
	font-weight: 500;
	color: $color-ink;
}

.product-card__tag {
	display: inline-block;
	margin-top: 16px;
	padding: 5px 13px;
	border-radius: 999px;
	background: $color-paper-alt;
	font-size: 0.76rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: $color-muted;
}
</style>
